<template>
  <div class="pa-6 b2c-v2-report">
    <h1 class="text-h5 font-weight-bold mb-1">MRR B2C / B2B2C</h1>
    <div class="d-flex align-center flex-wrap mb-4" style="gap: 12px;">
      <div class="body-2 grey--text text--darken-1">{{ tabSubtitle }}</div>
      <v-spacer />
      <v-select v-model="country" :items="countryOptions" label="País" outlined dense hide-details class="country-filter" />
    </div>

    <v-tabs v-model="tab" class="mb-4">
      <v-tab v-for="t in tabs" :key="t.key" :href="`#${t.key}`">{{ t.label }}</v-tab>
    </v-tabs>

    <v-tabs-items v-model="tab" class="transparent">
      <!-- Las dos pestañas tienen la misma forma: la foto de hoy de lo que mide la pestaña (MRR o
           membresías) y debajo su gráfico Mes/Año por línea (Guille, 2026-11-03). -->
      <v-tab-item v-for="t in tabs" :key="t.key" :value="t.key">
        <div class="pw-section mb-2">{{ t.metric === 'mrr' ? 'MRR' : 'Membresías' }} al {{ displayDate(date) }}</div>
        <v-row dense>
          <v-col v-for="box in mainBoxes" :key="`${t.key}-${box.key}`" cols="12" md="6">
            <B2CV2KpiCard v-bind="cardProps(box, t.metric)" main :inverse="box.inverse" @select="openDetail(box)" />
          </v-col>
        </v-row>
        <v-row dense class="mb-4">
          <v-col v-for="box in partBoxes" :key="`${t.key}-${box.key}`" cols="12" sm="6" md="3">
            <B2CV2KpiCard v-bind="cardProps(box, t.metric)" @select="openDetail(box)" />
          </v-col>
        </v-row>

        <v-alert v-if="dataQualityNotes.length" type="info" outlined dense class="body-2 mb-4">
          <div v-for="note in dataQualityNotes" :key="note">{{ note }}</div>
        </v-alert>

        <B2CV2TrendPanel :metric="t.metric" :country="country" :today="today" :clickable="canSeeMemberships" />
      </v-tab-item>
    </v-tabs-items>

    <B2CV2DetailDialog
      v-model="detail.open"
      :title="detail.title"
      :subtitle="detailSubtitle"
      :contracts="detail.contracts"
      :contract="detail.contract"
      :rows="detail.rows"
      :loading="detail.loading"
      :error="detail.error"
      :show-comment="detail.box === 'loyalty' && can('mrr-b2c-b2b2c:update')"
      :box="detail.box"
      :can-open-memberships="canSeeMemberships"
      @select-contract="openContract"
      @back="detail.contract = null"
      @save-note="saveNote"
    />

    <v-snackbar v-model="errorSnackbar" color="error" timeout="5000">{{ errorMessage }}</v-snackbar>
    <v-snackbar v-model="successSnackbar" color="success" timeout="3000">Comentario guardado</v-snackbar>
  </div>
</template>

<script>
import B2CV2KpiCard from '@/components/b2c-report-v2/B2CV2KpiCard.vue';
import B2CV2DetailDialog from '@/components/b2c-report-v2/B2CV2DetailDialog.vue';
import B2CV2TrendPanel from '@/components/b2c-report-v2/B2CV2TrendPanel.vue';
import B2CReportV2Service from '@/services/b2c-report-v2.service';
import { formatNumber } from '@/utils/b2c-report.utils';
import { COUNTRY_OPTIONS } from '@/utils/b2c-report-v2.labels';
import { can } from '@/utils/permissions';

/** Hoy en Chile, como YYYY-MM-DD: la fecha de corte es un día chileno, igual que en el backend. */
export const santiagoToday = (now = new Date()) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Santiago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(now);

// Pestañas de la vista; la elegida queda en `?tab=` para sobrevivir a la recarga.
// Spec 041: una pestaña por métrica. Cualquier otro `?tab=` (incluidos los antiguos) abre MRR.
// `clickHint` se muestra solo si en esa pestaña hay algo que abrir (depende de los permisos); el
// gráfico Mes/Año lleva su propio aviso.
const TABS = [
  {
    key: 'mrr',
    label: 'MRR',
    metric: 'mrr',
    subtitle: 'MRR de hoy y su evolución por línea de negocio, día a día o mes a mes.'
  },
  {
    key: 'membresias',
    label: 'Membresías',
    metric: 'count',
    subtitle: 'Membresías activas hoy y su evolución por línea de negocio.',
    clickHint: 'Haz clic en una cantidad para ver el listado.'
  }
];
const DEFAULT_TAB = 'mrr';

const emptyKpi = () => ({ count: 0, mrrUsd: 0, contracts: [] });

// Cajas que se abren por contrato antes de llegar a las membresías (pedido de Guille). B2C se
// abre por canal: Directo y cada broker.
const BOXES_BY_CONTRACT = ['total', 'bci', 'b2b2c', 'upsell'];

// Fila "Directo" de la lista de B2C: se pide como grupo, no por contrato.
const DIRECT_GROUP = 'direct';

// Valor del filtro de contrato para las membresías sin convenio (igual que el backend).
const NO_CONTRACT = 'none';

const emptyDetail = () => ({
  open: false,
  box: null,
  title: '',
  contracts: [],
  contract: null,
  rows: [],
  loading: false,
  error: ''
});

const emptySnapshot = () => ({
  date: null,
  kpis: {
    total: emptyKpi(),
    bci: emptyKpi(),
    b2c: emptyKpi(),
    b2b2c: emptyKpi(),
    upsell: emptyKpi(),
    loyalty: emptyKpi()
  },
  segments: [],
  b2cGroups: [],
  excluded: { upsell: { count: 0, bci: 0 } },
  dataQuality: { unknownChannel: 0, missingHistory: 0 }
});

export default {
  name: 'B2CReportV2',
  components: { B2CV2KpiCard, B2CV2DetailDialog, B2CV2TrendPanel },
  data() {
    const today = santiagoToday();
    const queryTab = this.$route?.query?.tab;
    const tab = TABS.some((t) => t.key === queryTab) ? queryTab : DEFAULT_TAB;
    return {
      tabs: TABS,
      tab,
      today,
      // Las tarjetas son la foto de hoy (Guille, 2026-11-03: sin selector de fecha de corte).
      date: today,
      // Chile por defecto (pedido de Guille); "Todos" sigue disponible en el filtro.
      country: 'CL',
      countryOptions: COUNTRY_OPTIONS,
      loadingSnapshot: false,
      snapshot: emptySnapshot(),
      detail: emptyDetail(),
      // Sin estas etiquetas el backend devuelve los contratos vacíos y rechaza los listados.
      canSeeMemberships: can('mrr-b2c-b2b2c:view-memberships'),
      errorSnackbar: false,
      errorMessage: '',
      successSnackbar: false
    };
  },
  computed: {
    tabSubtitle() {
      const tab = TABS.find((t) => t.key === this.tab) || TABS[0];
      return tab.clickHint && this.canClickIn() ? `${tab.subtitle} ${tab.clickHint}` : tab.subtitle;
    },
    /** Las dos cajas principales: el total B2C/B2B2C y sus pólizas BCI (contraste invertido). */
    mainBoxes() {
      return [
        { key: 'total', label: 'B2C/B2B2C', details: this.partsOf('count') },
        { key: 'bci', label: 'BCI Seguros', details: this.partsOf('bci'), inverse: true }
      ];
    },
    /** Las cuatro partes: B2C (Directo + Brokers), B2B2C y Upsell suman el total; Loyalty va aparte. */
    partBoxes() {
      return [
        { key: 'b2c', label: 'B2C', details: this.b2cLines() },
        {
          key: 'b2b2c',
          label: 'B2B2C',
          details: [
            { label: 'Pago Directo', value: this.segmentCount('b2b2cDirectPay') },
            { label: 'por Planilla', value: this.segmentCount('b2b2cPayroll') }
          ]
        },
        {
          key: 'upsell',
          label: 'Upsell de contratos B2B',
          details: this.snapshot.kpis.upsell.contracts.map((c) => ({
            label: c.companyName || c.contractCode || 'Sin contrato',
            value: formatNumber(c.count)
          }))
        },
        { key: 'loyalty', label: 'Pawer Loyalty', details: [] }
      ];
    },
    detailSubtitle() {
      const country = this.countryOptions.find((o) => o.value === this.country)?.text || 'Todos';
      return `Al ${this.displayDate(this.date)} · ${country}`;
    },
    dataQualityNotes() {
      const dq = this.snapshot.dataQuality || {};
      const notes = [];
      if (dq.unknownChannel) {
        notes.push(`${formatNumber(dq.unknownChannel)} membresías sin canal reconocible.`);
      }
      if (dq.missingHistory) {
        notes.push(`${formatNumber(dq.missingHistory)} membresías se desactivaron sin registro en el historial de estados.`);
      }
      return notes;
    }
  },
  watch: {
    // Los gráficos recargan solos con su prop `country`; aquí, las tarjetas.
    country: 'loadSnapshot',
    tab(value) {
      if (this.$route && this.$route.query.tab !== value) {
        this.$router.replace({ query: { ...this.$route.query, tab: value } });
      }
    }
  },
  mounted() {
    this.loadSnapshot();
  },
  methods: {
    can,
    formatNumber,
    /**
     * Props de una tarjeta según la pestaña: en Membresías la cantidad (clickeable, abre su
     * listado) y el detalle por parte; en MRR el monto en USD.
     */
    cardProps(box, metric) {
      const kpi = this.snapshot.kpis[box.key];
      if (metric === 'mrr') {
        return { label: box.label, value: kpi.mrrUsd, valueType: 'usd', loading: this.loadingSnapshot };
      }
      return {
        label: box.label,
        value: kpi.count,
        details: box.details,
        loading: this.loadingSnapshot,
        clickable: this.canOpenBox(box.key)
      };
    },
    displayDate(value) {
      if (!value) return '';
      const [y, m, d] = value.split('-');
      return `${d}/${m}/${y}`;
    },
    segmentCount(key) {
      return formatNumber(this.snapshot.segments.find((s) => s.segment === key)?.count || 0);
    },
    segmentOf(key) {
      return this.snapshot.segments.find((s) => s.segment === key) || {};
    },
    /**
     * Las tres partes del total (B2C, B2B2C y Upsell). `field` = 'count' para todas,
     * 'bci' para sus pólizas BCI.
     */
    partsOf(field) {
      const b2c = (this.segmentOf('b2cDirect')[field] || 0) + (this.segmentOf('b2cBrokers')[field] || 0);
      const b2b2c = (this.segmentOf('b2b2cDirectPay')[field] || 0) + (this.segmentOf('b2b2cPayroll')[field] || 0);
      const upsell = ((this.snapshot.excluded || {}).upsell || {})[field] || 0;
      return [
        { label: 'B2C', value: formatNumber(b2c) },
        { label: 'B2B2C', value: formatNumber(b2b2c) },
        { label: 'Upsell', value: formatNumber(upsell) }
      ];
    },
    /** B2C = Directo + Brokers integrados + Felix Dedicado ("Sin modalidad" solo si hay). */
    b2cLines() {
      const modality = (key) => (this.segmentOf('b2cBrokers').modalities || []).find((m) => m.modality === key) || {};
      const lines = [
        { label: 'Directo', value: this.segmentOf('b2cDirect').count || 0 },
        { label: 'Integrados', value: modality('integrated').count || 0 },
        { label: 'Felix Dedicado', value: modality('dedicatedFelix').count || 0 },
        { label: 'Sin modalidad', value: modality('unclassified').count || 0, onlyIfAny: true }
      ];
      return lines
        .filter((l) => !l.onlyIfAny || l.value > 0)
        .map((l) => ({ label: l.label, value: formatNumber(l.value) }));
    },
    showError(message) {
      this.errorMessage = message;
      this.errorSnackbar = true;
    },
    /** Guarda el comentario de una membresía de Loyalty y lo refleja en la fila sin recargar. */
    async saveNote({ membershipId, note }) {
      const result = await B2CReportV2Service.updateLoyaltyNote(membershipId, note);
      if (result.status !== 'ok') {
        this.showError(result.message);
        return;
      }
      const row = this.detail.rows.find((r) => r.membershipId === membershipId);
      if (row) row.comment = result.data.note;
      this.successSnackbar = true;
    },
    async loadSnapshot() {
      this.loadingSnapshot = true;
      const result = await B2CReportV2Service.getSnapshot({ date: this.date, country: this.country });
      this.loadingSnapshot = false;
      if (result.status !== 'ok') {
        this.showError(result.message);
        return;
      }
      this.snapshot = result.data;
    },
    /**
     * Contratos (o canales, para B2C) con que se abre una caja. Vienen vacíos sin
     * `mrr-b2c-b2b2c:view-contracts`.
     */
    contractsOf(key) {
      if (key === 'b2c') return this.snapshot.b2cGroups || [];
      if (BOXES_BY_CONTRACT.includes(key)) return this.snapshot.kpis[key].contracts;
      return [];
    },
    /** Una caja se abre si hay contratos que mostrar o si se pueden ver sus membresías. */
    canOpenBox(key) {
      return this.contractsOf(key).length > 0 || this.canSeeMemberships;
    },
    /** Si en la pestaña de Membresías hay alguna caja que se pueda abrir. */
    canClickIn() {
      return [...this.mainBoxes, ...this.partBoxes].some((box) => this.canOpenBox(box.key));
    },
    /**
     * Total, BCI, B2B2C y Upsell abren primero sus contratos; B2C, sus canales. Sin contratos
     * (Loyalty, o sin permiso para verlos) se va directo a las membresías de toda la caja.
     */
    async openDetail(box) {
      this.detail = { ...emptyDetail(), open: true, box: box.key, title: box.label };
      const contracts = this.contractsOf(box.key);
      if (contracts.length) {
        this.detail.contracts = contracts;
        return;
      }
      await this.loadRows(box.key, {});
    },
    /** Abre un contrato de la lista, o un canal de la lista de B2C ("Directo" o un broker). */
    async openContract(contract) {
      this.detail.contract = contract;
      const filter = contract.key === DIRECT_GROUP
        ? { group: DIRECT_GROUP }
        : { contract: contract.contractCode || NO_CONTRACT };
      await this.loadRows(this.detail.box, filter);
    },
    async loadRows(box, filter) {
      this.detail.rows = [];
      this.detail.loading = true;
      this.detail.error = '';
      const opened = this.detail.contract;
      const result = await B2CReportV2Service.getDetail({ box, ...filter, date: this.date, country: this.country });
      // Si cerraron, cambiaron de caja o de contrato mientras cargaba, esta respuesta ya no aplica.
      if (this.detail.box !== box || this.detail.contract !== opened) return;
      this.detail.loading = false;
      if (result.status !== 'ok') {
        this.detail.error = result.message;
        return;
      }
      this.detail.rows = result.data;
    }
  }
};
</script>

<style scoped>
.b2c-v2-report {
  max-width: 1400px;
}
.country-filter {
  max-width: 220px;
}
</style>
